import React, { useState, useEffect, useRef } from 'react';
import { Box, Paper, ToggleButton, ToggleButtonGroup, Select, MenuItem, IconButton, Popover, Tooltip, Typography, Divider, FormControl } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import ColorPicker from '../../ColorPicker';

const FONT_SIZES = [
  { label: 'Normal', value: '3' }, // 16px approx
  { label: 'Küçük', value: '2' }, // 13px approx
  { label: 'Büyük', value: '4' }, // 18px approx
  { label: 'Çok Büyük', value: '5' }, // 24px approx
  { label: 'Devasa', value: '6' }, // 32px approx
];

const RichTextEditor = ({ value, onChange, placeholder, minHeight = '100px' }) => {
  const editorRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [fontSize, setFontSize] = useState('3');
  const [isBold, setIsBold] = useState(false);
  const [savedSelection, setSavedSelection] = useState(null);

  // Sync value to editor content
  useEffect(() => {
    if (editorRef.current) {
      if (editorRef.current.innerHTML !== value) {
        // Only update if content is different to avoid cursor jumping
        // Simple check might fail if HTML structure changes but text is same
        // But for initial load it's fine.
        // For updates while typing, we don't want to re-set innerHTML usually.
        // We'll trust onChange handles the state update and we only set here if it's completely new or external change.
        // To be safe, we only set if editor is empty or strictly different and we are not focused (or it's initial)

        // Better approach for controlled component with ContentEditable is tricky.
        // Let's just set it on mount or if value changes externally (complex to detect).
        // For now, we will set it if the editor is empty, OR if the value passed in is significantly different 
        // and we aren't currently typing (hard to know).
        // Simplest: Just set it on mount and when value changes from outside (e.g. selecting different story).
        // We can compare stripped text? No.

        // Let's try: if editor.innerHTML != value.
        // This might cause cursor issues if we update `value` on every keystroke.
        // We will optimistically ONLY set it if they differ and we likely assume it's an external update.
        // A common pattern is to only update if document.activeElement !== editorRef.current

        if (document.activeElement !== editorRef.current) {
          editorRef.current.innerHTML = value || '';
        }
      }
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current && onChange) {
      onChange(editorRef.current.innerHTML);
    }
    checkSelection();
  };

  const checkSelection = () => {
    // Update toolbar state based on current selection
    const bold = document.queryCommandState('bold');
    setIsBold(bold);

    const size = document.queryCommandValue('fontSize');
    if (size) setFontSize(size);
  };

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel.rangeCount > 0) {
      setSavedSelection(sel.getRangeAt(0));
    }
  };

  const restoreSelection = () => {
    if (savedSelection) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedSelection);
    }
  };

  const handleBoldClick = () => {
    document.execCommand('bold', false, null);
    editorRef.current.focus();
    checkSelection();
    handleInput();
  };

  const handleFontSizeChange = (event) => {
    const newSize = event.target.value;
    document.execCommand('fontSize', false, newSize);
    setFontSize(newSize);
    editorRef.current.focus();
    handleInput();
  };

  const handleColorClick = (event) => {
    saveSelection();
    setAnchorEl(event.currentTarget);
  };

  const handleColorClose = () => {
    setAnchorEl(null);
  };

  const handleColorChange = (color) => {
    restoreSelection();
    document.execCommand('foreColor', false, color);
    if (editorRef.current) {
      editorRef.current.focus();
    }
    handleColorClose();
    handleInput();
  };

  return (
    <Box sx={{
      border: '1px solid rgba(0, 0, 0, 0.23)',
      borderRadius: '4px',
      overflow: 'hidden',
      '&:hover': {
        border: '1px solid rgba(0, 0, 0, 0.87)'
      },
      '&:focus-within': {
        border: '2px solid #1976d2',
        margin: '-1px' // Prevent layout shift
      }
    }}>
      {/* Toolbar */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.25,
        p: 0.25,
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        bgcolor: '#f5f5f5',
        minHeight: '32px'
      }}>
        <Tooltip title="Kalın">
          <ToggleButton
            value="bold"
            selected={isBold}
            onChange={handleBoldClick}
            size="small"
            sx={{ border: 'none', p: 0.5 }}
          >
            <FormatBoldIcon fontSize="small" />
          </ToggleButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <FormControl size="small" variant="standard" sx={{ minWidth: 80 }}>
          <Select
            value={fontSize}
            onChange={handleFontSizeChange}
            disableUnderline
            displayEmpty
            sx={{
              '& .MuiSelect-select': {
                py: 0.5,
                fontSize: '0.875rem'
              }
            }}
            renderValue={(selected) => {
              const size = FONT_SIZES.find(s => s.value === selected);
              return size ? size.label : 'Boyut';
            }}
          >
            {FONT_SIZES.map((size) => (
              <MenuItem key={size.value} value={size.value}>
                <span style={{ fontSize: ['10px', '13px', '16px', '18px', '24px', '32px'][size.value - 1] || '16px' }}>
                  {size.label}
                </span>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Tooltip title="Renk">
          <IconButton onClick={handleColorClick} size="small" sx={{ p: 0.5 }}>
            <FormatColorFillIcon fontSize="small" />
            {/* Color indicator underline */}
          </IconButton>
        </Tooltip>

        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleColorClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <Box sx={{ p: 1 }}>
            <ColorPicker color="#000000" onChange={handleColorChange} />
          </Box>
        </Popover>

      </Box>

      {/* Editor Area */}
      <Box
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onKeyUp={checkSelection}
        onMouseUp={checkSelection}
        sx={{
          minHeight: minHeight,
          p: 2,
          outline: 'none',
          '&:empty:before': {
            content: `"${placeholder || ''}"`,
            color: 'text.disabled'
          }
        }}
      />
    </Box >
  );
};


export default RichTextEditor;
